import React, { useCallback, useMemo } from "react";
import { Theme, useTheme, Popover, MenuItem, alpha } from "@mui/material";
import {
  BulkActionType,
  FlightRemovalType,
  IBulkEventPayload,
  IFlight,
  IGroundEvent,
} from "@vmosolutions/oc-interfaces";
import { useDispatch } from "react-redux";
import { openHistoryWindow, openSelectedEventWindow, openOrFocusOpsLinkWindow } from "src/redux/actions/Mosaic.Actions";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { setFocusedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { bulkEvent } from "src/redux/actions/flights/FlightData.Actions";

/** Flight/Event chip context menu properties  */
interface IChipContext {
  /** The incoming flight or ground event data */
  event: IFlight | IGroundEvent;
  /** Anchor Element used to anchor the context menu, null closes menu */
  anchorEl: HTMLDivElement;
  /** Used to close the menu by passing a null value back to parent */
  setAnchor: (anchor: any) => void;
  setParentAnchor?: React.Dispatch<React.SetStateAction<Element>>;
}

const chipContextMenuStyles = (theme: Theme): object => ({
  boxShadow: theme.shadows[1],
  borderRadius: "3px",
  backgroundColor: alpha(theme.palette.common.black, 0.85),
  "& .event_popover_menu": {
    padding: 0,
    "& li": {
      fontSize: 12,
      "&:hover": { color: theme.palette.primary.contrastText },
    },
  },
});

/**
 * Component used to display a small context menu when right clicking on the flight and event chips.
 * Currently only displays an option to edit but will have more options in the future (including focus on timeline etc etc)
 * @param props - IChipContext
 */
export const ChipContextMenu = (props: IChipContext): JSX.Element => {
  const { event, anchorEl, setAnchor, setParentAnchor } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  const handleOpsLinkEvent = useCallback(
    (flight: IFlight): void => {
      dispatch(openOrFocusOpsLinkWindow(flight));
    },
    [dispatch]
  );

  const handleEditEvent = useCallback(
    (event: IFlight | IGroundEvent): void => {
      if (setParentAnchor) {
        setAnchor(null);
        setParentAnchor(null);
      }
      dispatch(openSelectedEventWindow(event, []));
    },
    [dispatch]
  );

  const handleUncancelEvent = useCallback(
    (event: IFlight | IGroundEvent): void => {
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UNCANCEL_FLIGHT,
        batchIds: [event._id],
        payload: {}, //not gathering any info here
      };
      dispatch(bulkEvent(bulkPayload, 1));
    },
    [dispatch]
  );

  const handleViewFlightHistory = useCallback(
    (event: IFlight | IGroundEvent, eventType: "Flight" | "Ground Event"): void => {
      const props = {
        collection: eventType === "Flight" ? "flights" : "groundevents",
        id: event._id,
        flight: eventType === "Flight" ? (event as IFlight) : undefined,
        groundEvent: eventType === "Ground Event" ? (event as IGroundEvent) : undefined,
      };
      dispatch(openHistoryWindow(`${props.collection === "flights" ? "Flight" : "Ground Event"} History`, props, []));
    },
    [dispatch]
  );

  const eventType = useMemo(() => (isFlight(event) ? "Flight" : "Ground Event"), [event]);

  //check to make sure you have the event fields to jump to flight in timeline
  const canJumpToFlight = useMemo(
    () =>
      isFlight(event)
        ? ((event.actualOff &&
            event.actualOn &&
            (!event.removalType || event.removalType === FlightRemovalType.CancelFlight)) ??
          false)
        : ((event.actualStart && event.actualEnd) ?? false),
    [event]
  );

  //dont allow edit a deleted flight)
  const canEditEvent = useMemo(
    () =>
      isFlight(event) && !!event.removalType && event.removalType === FlightRemovalType.DeleteFlight ? false : true,
    [event]
  );

  const canUncancelEvent = useMemo(
    () =>
      isFlight(event) && !!event.removalType && event.removalType === FlightRemovalType.CancelFlight ? true : false,
    [event]
  );
  // jump to item on timeline
  const handleFocusEvent = useCallback(
    (event: IFlight | IGroundEvent): void => {
      dispatch(setFocusedEvents({ events: [event], shouldFocus: "first" }));
    },
    [dispatch]
  );

  /** If user has opened the context menu and moves outside of it, close it out. */
  // TODO Look into adding a delayed timer so that menu doesn't disappear so quickly on mouse out (with reset if they move back in)
  const handleCloseContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if (event) {
        setAnchor(null);
        if (setParentAnchor) setParentAnchor(null);
      }
    },
    [setAnchor, setParentAnchor]
  );

  return (
    <Popover
      id={`${event ? event._id : "empty"}-event_menu`}
      style={{ zIndex: 9999 }}
      open={!!anchorEl}
      onClose={() => {
        setAnchor(null);
        if (setParentAnchor) setParentAnchor(null);
      }}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: chipContextMenuStyles(theme),
        },
      }}
    >
      <div
        className="event_popover_menu"
        onMouseLeave={handleCloseContextMenu}
        onClick={(event: any) => event.stopPropagation()}
      >
        {!!canJumpToFlight && <MenuItem onClick={() => handleFocusEvent(event)}>{`Jump to ${eventType}`}</MenuItem>}
        {canEditEvent && <MenuItem onClick={() => handleEditEvent(event)}>{`Edit ${eventType}`}</MenuItem>}
        {canUncancelEvent && <MenuItem onClick={() => handleUncancelEvent(event)}>{`Uncancel ${eventType}`}</MenuItem>}
        <MenuItem onClick={() => handleViewFlightHistory(event, eventType)}>View {eventType} History</MenuItem>
        {eventType === "Flight" && (
          <MenuItem onClick={() => handleOpsLinkEvent(event as IFlight)}>Open OpsLink</MenuItem>
        )}
      </div>
    </Popover>
  );
};
